"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isMoapBuildSliderConfigFailure = isMoapBuildSliderConfigFailure;
exports.extractMoapSliderOfferConstants = extractMoapSliderOfferConstants;
exports.buildMoapSliderConfig = buildMoapSliderConfig;
exports.getMoapSliderSelection = getMoapSliderSelection;
const isNil_1 = __importDefault(require("lodash/isNil"));
const offerQuote_1 = require("../offerQuote");
const math_1 = require("./math");
const offerDetails_1 = require("./offerDetails");
function isMoapBuildSliderConfigFailure(result) {
    return 'error' in result;
}
function tryExtractMoapOfferConstants(offer) {
    if (!offer.identifier) {
        return { error: 'identifier is falsy' };
    }
    if (!offer.uwSpecId) {
        return { error: 'uwSpecId is falsy' };
    }
    // MOAP is variable-APR: require the revolving APR, not a fixed term.
    if (offer.apr === undefined || offer.apr === null) {
        return { error: 'apr is nil' };
    }
    const oneTimeFeeDecimal = offer.originationFeePercent ?? 0;
    if ((0, isNil_1.default)(offer.recordingChargesFixedComponentDollars) || (0, isNil_1.default)(offer.recordingChargesVariableComponentDecimal)) {
        return { error: 'recordingCharges components nil' };
    }
    const refiPayoffCents = (0, offerDetails_1.getOfferRefiPayoffCents)(offer);
    const refiTerms = offer.lienRefiOfferTerms;
    const priorLienPayoffFee = refiTerms?.payoffFee ?? offer.priorLienPayoffFee ?? 0;
    const payoffTermYears = (offer.drawPeriodInYears ?? 0) + (offer.repaymentOnlyPeriodInYears ?? 0);
    if (refiPayoffCents > 0 && (!refiTerms || (0, isNil_1.default)(offer.drawPeriodInYears) || (0, isNil_1.default)(offer.repaymentOnlyPeriodInYears) || payoffTermYears <= 0)) {
        return { error: 'canonical refi payoff terms or maturity period missing' };
    }
    // Every slider policy prices in bands, and the first band already starts at every floor underwriting applied.
    if (!offer.lineSizePricingBands || offer.lineSizePricingBands.length === 0) {
        return { error: 'lineSizePricingBands is empty' };
    }
    const sharedConstants = {
        identifier: offer.identifier,
        uwSpecId: offer.uwSpecId,
        apr: offer.apr,
        oneTimeFeeDecimal,
        recordingTaxFixedCents: (0, math_1.roundDecimalProduct)(offer.recordingChargesFixedComponentDollars, 100),
        recordingTaxVariableDecimal: offer.recordingChargesVariableComponentDecimal,
        balanceTransferFeePercent: offer.balanceTransferFeePercent ?? 0,
        recordingTaxFree: false,
        refiPayoffCents,
        refiPayoffWithFeeCents: (0, offerQuote_1.getRefiPayoffWithFeeCents)({ totalCents: refiPayoffCents }, priorLienPayoffFee),
        payoffFixedApr: refiTerms?.payoffFixedApr ?? null,
        payoffFixedTermMonthlyPayment: refiTerms?.payoffFixedTermMonthlyPayment ?? null,
        payoffFixedTermMonths: refiTerms ? payoffTermYears * 12 : null,
        refiPositions: (0, offerDetails_1.getOfferRefiPositions)(offer),
        refiLiensDetails: offer.lienRefiOfferTerms?.refiLiensDetails ?? [],
        refiLenderName: (0, offerDetails_1.getOfferRefiLenderName)(offer),
        secondLienBalanceBufferPercent: offer.refiLineSizeBufferDecimal ?? offer.helocRefiDetails?.secondLienBalanceBufferPercent ?? 0,
    };
    const banded = [];
    for (const band of [...offer.lineSizePricingBands].sort((l, r) => l.minLineSize - r.minLineSize)) {
        const minLineSizeCents = (0, math_1.roundDecimalProduct)(band.minLineSize, 100);
        const maxLineSizeCents = (0, math_1.roundDecimalProduct)(band.maxLineSize, 100);
        if (minLineSizeCents > maxLineSizeCents) {
            continue;
        }
        if (band.pricing.apr === null || band.pricing.apr === undefined) {
            return { error: `line-size pricing band ${band.minLineSize}-${band.maxLineSize} has nil apr` };
        }
        banded.push({ ...sharedConstants, apr: band.pricing.apr, pricing: band.pricing, minLineSizeCents, maxLineSizeCents, recordingTaxFree: band.recordingTaxFree ?? false });
    }
    if (banded.length === 0) {
        return { error: 'lineSizePricingBands have no selectable line' };
    }
    return banded;
}
function extractMoapSliderOfferConstants(offers, drops) {
    const all = [];
    for (const offer of offers) {
        const result = tryExtractMoapOfferConstants(offer);
        if ('error' in result) {
            drops?.push({ identifier: offer.identifier, error: result.error });
        }
        else {
            all.push(...result);
        }
    }
    return all;
}
/**
 * Build the MOAP slider config from the underwriting result + backend-computed debt-swap payoff.
 *
 * Fails loudly when the offers cannot be reconciled with the local variable-APR math (same
 * "never soften a failure into a clamp" contract as the AC builder): a failure means the
 * frontend must render the approved terms read-only rather than an interactive slider.
 */
function buildMoapSliderConfig(underwritingResult, debtSwapPayoffCents) {
    const totalOffers = underwritingResult.preQualOffers?.length ?? 0;
    const drops = [];
    const fail = (error) => ({ error, totalOffers, drops });
    const constants = extractMoapSliderOfferConstants(underwritingResult.preQualOffers ?? [], drops);
    if (constants.length === 0) {
        return fail('all offers dropped by extractMoapSliderOfferConstants');
    }
    const feasibleOffers = [];
    for (const offer of constants) {
        const debtSwapFeeCents = (0, offerDetails_1.computeBalanceTransferFee)(debtSwapPayoffCents, offer.balanceTransferFeePercent).amountCents;
        const payoffAndDebtSwapCents = offer.refiPayoffWithFeeCents + debtSwapPayoffCents + debtSwapFeeCents;
        // Round the additional-limit floor (balance buffer + recording) of a refinance up to the nearest $1,000.
        const min = offer.refiPayoffCents > 0 ? payoffAndDebtSwapCents + Math.ceil((offer.minLineSizeCents - payoffAndDebtSwapCents) / 100000) * 100000 : offer.minLineSizeCents;
        if (min <= offer.maxLineSizeCents) {
            feasibleOffers.push({ ...offer, minLineSizeCents: min });
        }
    }
    if (feasibleOffers.length === 0) {
        return fail('committed payoffs and fees exceed all approved lines');
    }
    return {
        minLineSizeCents: Math.min(...feasibleOffers.map((offer) => offer.minLineSizeCents)),
        maxLineSizeCents: Math.max(...feasibleOffers.map((offer) => offer.maxLineSizeCents)),
        sliderOfferConstants: feasibleOffers,
    };
}
/**
 * Select one feasible offer consistently for all displayed and submitted terms: the nearest line, then the lowest APR, then the
 * lowest fees of the offer quoted at that line. An offer that cannot be quoted there sorts last; the caller's own quote shows its error.
 */
function getMoapSliderSelection(config, offers, requestedLineSizeCents) {
    let selected = null;
    let selectedOffer = null;
    let selectedLineSizeCents = 0;
    let selectedDistance = Infinity;
    let selectedFees = Infinity;
    for (const constants of config.sliderOfferConstants) {
        const offer = offers.find((candidate) => candidate.identifier === constants.identifier && candidate.uwSpecId === constants.uwSpecId);
        if (!offer) {
            continue;
        }
        const lineSizeCents = Math.max(constants.minLineSizeCents, Math.min(constants.maxLineSizeCents, Math.round(requestedLineSizeCents)));
        const distance = Math.abs(lineSizeCents - requestedLineSizeCents);
        const { quote } = (0, offerQuote_1.tryQuoteOffer)(offer, lineSizeCents);
        const fees = quote
            ? quote.upfrontFeesCents + quote.recordingFeeCents + quote.refiPayoffFeeCents + (0, offerDetails_1.computeBalanceTransferFee)(quote.debtSwapPayoff.totalCents, constants.balanceTransferFeePercent).amountCents
            : Infinity;
        if (distance < selectedDistance || (distance === selectedDistance && (!selected || constants.apr < selected.apr || (constants.apr === selected.apr && fees < selectedFees)))) {
            selected = constants;
            selectedOffer = offer;
            selectedLineSizeCents = lineSizeCents;
            selectedDistance = distance;
            selectedFees = fees;
        }
    }
    return selected && selectedOffer ? { constants: selected, offer: selectedOffer, lineSizeCents: selectedLineSizeCents } : null;
}
